import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
  PortalOverlayModal,
  Stack,
  TextFormField,
  BodyText,
} from '../../../ui';
import { useIsMobile, useOnce } from '../../../../hooks';
import { ServiceGroupHeader } from '../ServiceList/ServiceGroupHeader';
import { ServiceItem } from '../ServiceItem/ServiceItem';
import { BasicSearchIcon } from '../../../ui/Icons';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { ProviderServiceGroup } from '../../../../api/Providers/ServiceGroups';
import { ProviderService } from '../../../../api/Providers/Services';
import { RootState } from '../../../../store/models';
import { buildPopularServices } from '../serviceSelectors';

export const SearchServiceModal: React.FC<{
  serviceGroups: ProviderServiceGroup[];
  onCancel: () => void;
  profile: ClientProviderProfile;
}> = ({
  serviceGroups, onCancel, profile,
}) => {
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [serviceGroupsWithRanking, setServiceGroupsWithRanking] = React.useState<
  ProviderServiceGroup[]
  >([]);

  const currentProId = profile.id;
  const isMobile = useIsMobile();

  const filteredServices = useMemo(() => {
    if (searchQuery) {
      const filteredServWithGroups: ProviderServiceGroup[] = serviceGroupsWithRanking.map(
        serviceGroup => ({
          ...serviceGroup,
          services: serviceGroup.services.filter((service: ProviderService) => service
            .name
            .toLowerCase().includes(searchQuery.toLowerCase())),
        }),
      );
      const filteredGroups = filteredServWithGroups.filter(
        serviceGroup => serviceGroup.services.length,
      );
      return filteredGroups;
    }
    return serviceGroupsWithRanking;
  }, [
    searchQuery,
    serviceGroupsWithRanking,
  ]);
  const popularServices = useSelector<RootState, ProviderService[]>(
    state => buildPopularServices(state, profile),
  );
  useOnce(() => {
    const popularServicesDict = {};
    popularServices.forEach((service, index) => {
      popularServicesDict[service.id] = {
        ...service,
        serviceRank: index + 1,
      };
    });

    const enrichedServiceGroups = serviceGroups.map(serviceGroup => ({
      ...serviceGroup,
      services: serviceGroup.services.map(service => ({
        ...service,
        serviceRank: popularServicesDict?.[service.id]?.serviceRank || null,
      })),
    }));
    setServiceGroupsWithRanking(enrichedServiceGroups);
  }, popularServices || null);
  return (
    <PortalOverlay
      name="search-service"
      animationStyle={isMobile ? 'slideUp' : null}
    >
      <PortalOverlayModal
        position="menu"
        boxProps={{
          height: isMobile ? '100%' : 600,
        }}
      >
        <PortalOverlayHeader
          alignItems="center"
          hideBottomBorder
          renderRightAction={() => (
            <Box
              paddingTop={16}
              paddingRight={16}
            >
              <PortalOverlayHeaderCloseBtn
                noPadding
                onCloseRequest={onCancel}
              />
            </Box>
          )}
          renderLeftAction={() => (
            <Box
              paddingLeft={24}
              paddingTop={24}
            />
          )}
          renderTitle={() => (
            <Inline
              space={8}
              paddingTop={4}
              alignItems="center"
              justifyContent="center"
            >
              <BodyText appearance="large" weight="bold">Services</BodyText>
            </Inline>
          )}
        />
        <Stack
          space={20}
          paddingTop={20}
          overflow="hidden"
          height="100%"
          flex={1}
        >
          <Box paddingX={24} testID="service_search_modal_search_bar">
            <Inline
              space={8}
              paddingX={16}
              borderColor={BrandColor.Silver}
              borderWidth={1}
              borderRadius={100}
              borderStyle="solid"
              alignItems="center"
            >
              <BasicSearchIcon
                color={BrandColor.Metal}
              />
              <Box
                flex={1}
              >
                <TextFormField
                  value={searchQuery}
                  placeholder={`Search ${profile.name}'s Services`}
                  onChangeText={search => setSearchQuery(search)}
                  maxLength={250}
                  appearance="stretch-aligned"
                  style={{
                    borderWidth: 0,
                  }}
                />
              </Box>
            </Inline>
          </Box>
          <Stack
            paddingTop={20}
            paddingX={24}
            overflow="auto"
            height="100%"
            flex={1}
            testID="service_search_modal_search_results"
          >
            {filteredServices.map(serviceGroup => (
              <Stack key={serviceGroup.id}>
                <ServiceGroupHeader
                  serviceCount={serviceGroup.services.length}
                  serviceName={serviceGroup.name}
                />
                {serviceGroup.services.map(service => (
                  <ServiceItem
                    key={service.id}
                    isOnlineBookable={service.is_online_bookable}
                    providerId={currentProId}
                    service={service}
                    providerPhone={profile.public_phone_number}
                    displayPhotos={false}
                  />
                ))}
              </Stack>
            ))}
            {filteredServices.length === 0 && (
              <Box
                color={BrandColor.SuperLight}
                paddingX={24}
                paddingY={32}
                testID="service_search_modal_no_results"
              >
                <BodyText color={BrandColor.Ash} textAlign="center">
                  No services match your search term, please adjust and try again
                </BodyText>
              </Box>
            )}
          </Stack>
        </Stack>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};
